import { fetchJSON as fetchAPI } from './client';

export type ProfitCostComponent = {
  key: string; name: string; current_value?: string; historical?: string; benchmark?: string;
  target?: string; gap?: string; priority?: string; business_impact?: string; evidence?: string[];
};

export type TrueProfitSnapshot = {
  entity_id: string; entity_type: string; entity_name?: string;
  gross_revenue: string; net_profit: string; contribution_margin: string;
  margin_percent: string; cash_contribution: string; historical_profit?: string;
  projected_profit?: string; components: ProfitCostComponent[]; dna_origin: boolean; generated_at: string;
};

export type ContributionMarginSnapshot = {
  entity_id: string; cm1: string; cm2: string; cm3: string;
  contribution_pct: string; margin_pct: string; trend?: string; benchmark?: string; confidence: number;
};

export type LeakageItem = {
  id: string; type: string; title: string; evidence?: string[]; root_cause?: string;
  estimated_recovery?: string; confidence: number; priority: string;
  business_impact?: string; suggested_action?: string; status: string;
};

export type SettlementRecord = {
  id: string; period: string; total_amount: string; order_total?: string;
  variance?: string; status: string; matched_count?: number; unmatched_count?: number;
};

export type ReconciliationResult = {
  type: string; status: string; matched: number; unmatched: number;
  variance?: string; evidence?: string[]; suggested_resolution?: string;
};

export type RecoveryClaim = {
  id: string; type: string; title: string; expected_recovery?: string;
  recovered_amount?: string; pending_amount?: string; status: string; evidence_count?: number; created_at: string;
};

export type ProfitForecast = {
  entity_id: string; expected_revenue: string; gross_profit: string; net_profit: string;
  cash_contribution: string; confidence: number; forecast_drivers?: string[];
  alternative_low?: string; alternative_high?: string; generated_at: string;
};

export type FinancialScenario = {
  id: string; name: string; type: string; revenue?: string; profit?: string;
  cash_flow?: string; working_capital?: string; recovery?: string; confidence: number; key_assumptions?: string[];
};

export type FinancialRecommendation = {
  id: string; title: string; category: string; business_objective: string;
  evidence?: string[]; root_cause?: string; expected_recovery?: string; expected_profit?: string;
  expected_cash_flow_improvement?: string; confidence: number; priority: string;
  effort: string; risk: string; status: string; action_label: string; dna_evidence: boolean;
};

export type ProfitOpportunity = {
  id: string; title: string; type: string; expected_recovery?: string;
  expected_profit?: string; priority: string; confidence: number;
};

export type FinancialVersion = {
  id: string; entity_id: string; version: number; type: string; status: string;
  author?: string; approved: boolean; created_at: string; summary?: string;
};

export type FinancialExperimentLink = {
  id: string; entity_id: string; name: string; status: string;
  baseline_kpi?: string; observation_window?: string; created_at: string;
};

export type ProfitTimelineEvent = {
  id: string; entity_id: string; type: string; title: string; actor?: string; timestamp: string;
};

const COMMAND_BASE = import.meta.env.VITE_COMMAND_API ?? '/api/v1/command';
const fetchCmd = <T,>(path: string, init?: RequestInit) => fetchAPI<T>(`${COMMAND_BASE}${path}`, init);

export const profitIntelligenceApi = {
  profit: (entityId: string) => fetchCmd<TrueProfitSnapshot>(`/profit-intelligence/${encodeURIComponent(entityId)}/profit`),
  contribution: (entityId: string) => fetchCmd<ContributionMarginSnapshot>(`/profit-intelligence/${encodeURIComponent(entityId)}/contribution`),
  leakage: (entityId: string) =>
    fetchCmd<{ leakages: LeakageItem[] }>(`/profit-intelligence/${encodeURIComponent(entityId)}/leakage`).then((r) => r.leakages),
  settlements: (entityId: string) =>
    fetchCmd<{ settlements: SettlementRecord[] }>(`/profit-intelligence/${encodeURIComponent(entityId)}/settlements`).then((r) => r.settlements),
  reconciliation: (entityId: string) =>
    fetchCmd<{ reconciliation: ReconciliationResult[] }>(`/profit-intelligence/${encodeURIComponent(entityId)}/reconciliation`).then((r) => r.reconciliation),
  recovery: (entityId: string) =>
    fetchCmd<{ recovery: RecoveryClaim[] }>(`/profit-intelligence/${encodeURIComponent(entityId)}/recovery`).then((r) => r.recovery),
  forecast: (entityId: string) => fetchCmd<ProfitForecast>(`/profit-intelligence/${encodeURIComponent(entityId)}/forecast`),
  scenarios: (entityId: string) =>
    fetchCmd<{ scenarios: FinancialScenario[] }>(`/profit-intelligence/${encodeURIComponent(entityId)}/scenarios`).then((r) => r.scenarios),
  recommendations: (entityId: string) =>
    fetchCmd<{ recommendations: FinancialRecommendation[] }>(`/profit-intelligence/${encodeURIComponent(entityId)}/recommendations`).then((r) => r.recommendations),
  opportunities: (entityId: string) =>
    fetchCmd<{ opportunities: ProfitOpportunity[] }>(`/profit-intelligence/${encodeURIComponent(entityId)}/opportunities`).then((r) => r.opportunities),
  attribution: (entityId: string) =>
    fetchCmd<{ attribution: { factor: string; contribution: string; profit_impact?: string; data_type: string; confidence: number }[] }>(`/profit-intelligence/${encodeURIComponent(entityId)}/attribution`).then((r) => r.attribution),
  versions: (entityId: string) =>
    fetchCmd<{ versions: FinancialVersion[] }>(`/profit-intelligence/${encodeURIComponent(entityId)}/versions`).then((r) => r.versions),
  experiments: (entityId: string) =>
    fetchCmd<{ experiments: FinancialExperimentLink[] }>(`/profit-intelligence/${encodeURIComponent(entityId)}/experiments`).then((r) => r.experiments),
  history: (entityId: string) =>
    fetchCmd<{ history: ProfitTimelineEvent[] }>(`/profit-intelligence/${encodeURIComponent(entityId)}/history`).then((r) => r.history),
};
